(() => {
  'use strict';

  const RPTL_REBUY_BRIDGE_STATE = 'ready';
  const RPTL_FILTER_COMPONENTS = 'collection-component.search-page, collection-component[data-section-id$="__rptl_plp_standard_main"]';
  const RPTL_FILTER_TRIGGER = '[data-aria-toggle][aria-controls="filter-groups"]';
  const RPTL_FILTER_SIDEBAR = '[data-collection-sidebar]';
  const RPTL_FILTER_CLOSE = '[data-collection-sidebar-close]';
  const RPTL_FILTER_INERT = 'data-rptl-filter-inert';
  const RPTL_REBUY_LOCALIZED = 'localized';

  const localizeRebuyText = (root) => {
    const textTranslations = new Map([
      ['Re-Add', 'Añadir'],
      ['FINALIZAR PEDIDO →', 'Finalizar pedido'],
      ['FINALIZAR PEDIDO', 'Finalizar pedido'],
      ['Close Cart', 'Cerrar carrito'],
      ['Price', 'Precio'],
      ['Cart subtotal', 'Subtotal del carrito'],
    ]);
    const textPatternTranslations = [
      [/^Quantity of (.+)$/i, 'Cantidad de $1'],
      [/^Opening the cart\. There are (\d+) items? in your cart\.$/i, 'Carrito abierto. Hay $1 productos en tu carrito.'],
    ];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      const normalized = textNode.nodeValue.trim();
      const patternTranslation = textPatternTranslations.find(([pattern]) => pattern.test(normalized));
      const translated = textTranslations.get(normalized)
        || (patternTranslation && normalized.replace(patternTranslation[0], patternTranslation[1]));
      if (translated) {
        textNode.nodeValue = textNode.nodeValue.replace(normalized, translated);
      }
      textNode = walker.nextNode();
    }

    const attributeTranslations = [
      [/^Close Cart$/i, 'Cerrar carrito'],
      [/^Remove (.+)$/i, 'Eliminar $1'],
      [/^Decrease quantity of (.+)$/i, 'Disminuir cantidad de $1'],
      [/^Increase quantity of (.+)$/i, 'Aumentar cantidad de $1'],
      [/^Quantity of (.+)$/i, 'Cantidad de $1'],
      [/^View details for (.+)$/i, 'Ver detalles de $1'],
      [/^View (.+)$/i, 'Ver $1'],
      [/^Add the product, (.+) to Cart$/i, 'Añadir $1 al carrito'],
      [/^Select (.+)$/i, 'Seleccionar $1'],
      [/^Expand bundle of (.+)$/i, 'Ver productos de $1'],
      [/^Collapse bundle of (.+)$/i, 'Ocultar productos de $1'],
    ];

    for (const element of root.querySelectorAll('[aria-label], [title], [alt]')) {
      for (const attribute of ['aria-label', 'title', 'alt']) {
        const currentValue = element.getAttribute(attribute);
        if (!currentValue) continue;

        const translation = attributeTranslations.find(([pattern]) => pattern.test(currentValue));
        if (!translation) continue;

        element.setAttribute(attribute, currentValue.replace(translation[0], translation[1]));
      }
    }

    for (const readdButton of root.querySelectorAll('.rebuy-cart__progress-gift-variant-readd, .rebuy-cart__progress-free-product-variant-readd')) {
      if (!readdButton.hasAttribute('tabindex')) readdButton.tabIndex = 0;
      readdButton.setAttribute('aria-label', 'Añadir regalo nuevamente');
    }
  };

  const syncRebuyCartLayout = (cart) => {
    const gift = cart.querySelector('.rebuy-cart__progress-gifts, .rebuy-cart__progress-free-products');
    const firstItem = cart.querySelector('.rebuy-cart__flyout-item');
    const itemsList = firstItem?.closest('ul');
    const itemsSection = itemsList?.parentElement;
    const contentRoot = itemsSection?.parentElement;

    if (!gift || !contentRoot) return;

    const widget = contentRoot.querySelector('.rebuy-widget.widget-type-cart');
    let recommendationSection = widget;

    while (recommendationSection?.parentElement && recommendationSection.parentElement !== contentRoot) {
      recommendationSection = recommendationSection.parentElement;
    }

    gift.dataset.rptlRebuyPlacement = 'after-items';

    if (recommendationSection?.parentElement === contentRoot) {
      if (gift.parentElement !== contentRoot || gift.nextElementSibling !== recommendationSection) {
        contentRoot.insertBefore(gift, recommendationSection);
      }
      return;
    }

    if (gift.parentElement !== contentRoot || gift !== contentRoot.lastElementChild) {
      contentRoot.append(gift);
    }
  };

  const parseRebuyCurrency = (text) => {
    const compactValue = text.replace(/\s/g, '');
    const match = compactValue.match(/-?[\d.,]+/);

    if (!match) return null;

    const rawValue = match[0];
    const lastComma = rawValue.lastIndexOf(',');
    const lastPeriod = rawValue.lastIndexOf('.');
    let normalizedValue = rawValue;

    if (lastComma >= 0 && lastPeriod >= 0) {
      normalizedValue = lastComma > lastPeriod
        ? rawValue.replace(/\./g, '').replace(',', '.')
        : rawValue.replace(/,/g, '');
    } else if (lastComma >= 0) {
      normalizedValue = rawValue.length - lastComma - 1 <= 2
        ? rawValue.replace(',', '.')
        : rawValue.replace(/,/g, '');
    } else if (lastPeriod >= 0 && rawValue.length - lastPeriod - 1 > 2) {
      normalizedValue = rawValue.replace(/\./g, '');
    }

    const value = Number.parseFloat(normalizedValue);
    return Number.isFinite(value) ? value : null;
  };

  const syncRebuyShippingProgress = (cart) => {
    const progress = cart.querySelector(
      '.rebuy-cart__progress-bar-meter-fill, .rebuy-cart__flyout-shipping-bar-meter-fill',
    );
    const prompt = cart.querySelector('.rebuy-cart__progress-bar-prompt');
    const subtotal = cart.querySelector('.rebuy-cart__flyout-subtotal-amount');

    if (!progress || !prompt) return;

    const promptText = prompt.textContent.trim();
    const subtotalValue = parseRebuyCurrency(subtotal?.textContent || '') ?? 0;
    const remainingValue = parseRebuyCurrency(promptText);
    const isShippingGoal = /env[ií]o\s+gratis/i.test(promptText);
    let percentage = 100;

    if (isShippingGoal && remainingValue !== null) {
      const shippingGoal = subtotalValue + remainingValue;
      percentage = shippingGoal > 0 ? (subtotalValue / shippingGoal) * 100 : 0;
    }

    const boundedPercentage = Math.min(100, Math.max(0, percentage));
    const roundedPercentage = Math.round(boundedPercentage);
    const accessibleLabel = `Progreso para envío gratis: ${roundedPercentage}%`;
    const screenReaderLabel = progress.querySelector('.sr-only');

    progress.style.width = `${boundedPercentage.toFixed(4)}%`;
    progress.setAttribute('aria-valuenow', String(roundedPercentage));
    progress.setAttribute('aria-label', accessibleLabel);
    progress.dataset.rptlProgressGoal = 'free-shipping';

    if (screenReaderLabel && screenReaderLabel.textContent !== accessibleLabel) {
      screenReaderLabel.textContent = accessibleLabel;
    }
  };

  const syncRebuyCart = (cart) => {
    localizeRebuyText(cart);
    syncRebuyCartLayout(cart);
    syncRebuyShippingProgress(cart);
  };

  const initRebuyLocalization = () => {
    const cart = document.querySelector('#rebuy-cart');
    if (!cart || cart.dataset.rptlLocalization === RPTL_REBUY_LOCALIZED) return;

    syncRebuyCart(cart);
    new MutationObserver(() => syncRebuyCart(cart)).observe(cart, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    cart.addEventListener('keydown', (event) => {
      const readdButton = event.target instanceof Element
        ? event.target.closest('.rebuy-cart__progress-gift-variant-readd, .rebuy-cart__progress-free-product-variant-readd')
        : null;
      if (!readdButton || (event.key !== 'Enter' && event.key !== ' ')) return;

      event.preventDefault();
      readdButton.click();
    });
    cart.dataset.rptlLocalization = RPTL_REBUY_LOCALIZED;
  };

  const syncHeaderDrawerControls = () => {
    const hamburger = document.querySelector('[data-drawer-toggle="hamburger"]');
    const drawer = document.querySelector('.drawer--header[id]');

    if (!hamburger || !drawer) return;

    hamburger.setAttribute('aria-controls', drawer.id);
  };

  const setFilterBackgroundInert = (component, sidebar, isInert) => {
    if (!isInert) {
      for (const element of document.querySelectorAll(`[${RPTL_FILTER_INERT}]`)) {
        element.inert = false;
        element.removeAttribute(RPTL_FILTER_INERT);
      }
      return;
    }

    const trigger = component.querySelector(RPTL_FILTER_TRIGGER);
    const navigation = trigger?.closest('[data-collection-nav]');
    const background = new Set([
      ...document.querySelectorAll('.header-sections, .footer-sections'),
      ...Array.from(document.querySelectorAll('#MainContent > .shopify-section')).filter(
        (section) => !section.contains(sidebar),
      ),
      ...Array.from(component.children).filter(
        (element) => !element.contains(sidebar) && !element.contains(trigger),
      ),
      ...Array.from(navigation?.children || []).filter((element) => !element.contains(trigger)),
      ...Array.from(sidebar.parentElement?.children || []).filter((element) => element !== sidebar),
    ]);

    for (const element of background) {
      element.inert = true;
      element.setAttribute(RPTL_FILTER_INERT, '');
    }
  };

  const initFilterDrawerA11y = () => {
    const components = document.querySelectorAll?.(RPTL_FILTER_COMPONENTS) || [];

    for (const component of components) {
      const trigger = component.querySelector(RPTL_FILTER_TRIGGER);
      const sidebar = component.querySelector(RPTL_FILTER_SIDEBAR);
      if (!trigger || !sidebar || trigger.dataset.rptlFilterDrawerA11y === RPTL_REBUY_BRIDGE_STATE) continue;

      let wasOpen = false;
      const syncModalState = () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        if (!isOpen) {
          sidebar.removeAttribute('role');
          sidebar.removeAttribute('aria-modal');
          sidebar.removeAttribute('aria-label');

          if (wasOpen) {
            window.theme?.a11y?.removeTrapFocus();
            setFilterBackgroundInert(component, sidebar, false);
            if (document.contains(trigger)) trigger.focus();
          }
          wasOpen = false;
          return;
        }

        wasOpen = true;
        sidebar.setAttribute('role', 'dialog');
        sidebar.setAttribute('aria-modal', 'true');
        sidebar.setAttribute('aria-label', trigger.textContent.trim());

        const focusDrawer = () => {
          if (trigger.getAttribute('aria-expanded') !== 'true') return true;
          if (getComputedStyle(sidebar).visibility !== 'visible') return false;

          const close = sidebar.querySelector(RPTL_FILTER_CLOSE);
          const focusTarget = close || sidebar;
          window.theme?.a11y?.trapFocus(sidebar, {elementToFocus: focusTarget});
          focusTarget.focus({preventScroll: true});
          setFilterBackgroundInert(component, sidebar, true);
          return true;
        };
        const handleVisibleTransition = () => {
          if (!focusDrawer()) return;

          sidebar.removeEventListener('transitionstart', handleVisibleTransition);
          sidebar.removeEventListener('transitionend', handleVisibleTransition);
        };

        requestAnimationFrame(() => {
          if (focusDrawer()) return;

          sidebar.addEventListener('transitionstart', handleVisibleTransition);
          sidebar.addEventListener('transitionend', handleVisibleTransition);
        });
      };

      new MutationObserver(syncModalState).observe(trigger, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
      trigger.dataset.rptlFilterDrawerA11y = RPTL_REBUY_BRIDGE_STATE;
      syncModalState();
    }
  };

  const initOriginValuesSliderA11y = () => {
    const slider = document.querySelector('[id^="SectionColumns--"][id$="__rptl_origin_values"] .grid--mobile-slider');
    if (!slider || slider.dataset.rptlOriginSliderA11y === RPTL_REBUY_BRIDGE_STATE) return;

    const heading = slider.closest('[id^="SectionColumns--"]')?.querySelector('h2');
    if (!heading?.textContent.trim()) return;

    slider.tabIndex = 0;
    slider.setAttribute('role', 'region');
    slider.setAttribute('aria-label', heading.textContent.trim());
    slider.dataset.rptlOriginSliderA11y = RPTL_REBUY_BRIDGE_STATE;
  };

  const init = () => {
    syncHeaderDrawerControls();
    initRebuyLocalization();
    initFilterDrawerA11y();
    initOriginValuesSliderA11y();

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }

  document.addEventListener('rebuy:smartcart.ready', initRebuyLocalization);

  document.addEventListener('shopify:section:load', () => {
    syncHeaderDrawerControls();
    initRebuyLocalization();
    initFilterDrawerA11y();
    initOriginValuesSliderA11y();
  });
})();
